import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate, Navigate } from "react-router-dom";
import {
  getProductById,
  getStaticProductById,
  parseProductIdFromPath,
  isSupabaseProductRoute,
  isUuid,
  slugify,
  submitProductReview,
} from "../lib/productService";
import { useCartWishlist } from "../contexts/CartWishlistContext";
import "./gold-bracelet1.css";

export default function ProductDetailPage() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const productId = parseProductIdFromPath(pathname);

  // Read any pre-fetched data handed off by the previous page (Instant Loading!)
  const preloadedData = useLocation().state?.product || null;
  const staticProduct = getStaticProductById(productId);
  
  // Merge our instant knowledge to show a fully populated correct page instantly
  const initialProduct = preloadedData 
    ? { ...staticProduct, ...preloadedData, images: preloadedData.images || staticProduct?.images || [preloadedData.img] }
    : staticProduct;

  const [product, setProduct] = useState(initialProduct || null);
  // Only show the blocking loading screen if we have absolutely nothing to show yet
  const [loading, setLoading] = useState(!initialProduct);

  const [selectedImg, setSelectedImg] = useState(null);
  const sliderRef = useRef();

  // Rating Submission State
  const [userRating, setUserRating] = useState(() => {
    return Number(localStorage.getItem(`rated_${productId}`)) || 0;
  });
  const [oldUserRating, setOldUserRating] = useState(() => {
    return localStorage.getItem(`rated_${productId}`) ? Number(localStorage.getItem(`rated_${productId}`)) : null;
  });
  const [hoverRating, setHoverRating] = useState(0);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [reviewText, setReviewText] = useState("");
  const [submittedMsg, setSubmittedMsg] = useState("");

  // Cart & Wishlist from Supabase
  const {
    cart,
    addToCart,
    incQty,
    decQty,
    toggleWish,
    isWished,
  } = useCartWishlist();

  useEffect(() => {
    let cancelled = false;
    getProductById(productId)
      .then((p) => {
        // if p is undefined or null (network error), keep staticProduct as is.
        if (!cancelled && p) setProduct(p);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [productId]);

  useEffect(() => {
    if (product?.images?.[0]) setSelectedImg(product.images[0]);
  }, [product]);

  // Replace UUID in URL with slug (product name) for cleaner URLs
  useEffect(() => {
    if (!product || product.source !== "supabase") return;
    const slug = product.slug || slugify(product.title);
    if (slug && isUuid(productId) && pathname !== `/product/${slug}`) {
      navigate(`/product/${slug}`, { replace: true });
    }
  }, [product, productId, pathname, navigate]);

  if (loading) {
    return (
      <div className="product-page" style={{ padding: "80px 20px", textAlign: "center" }}>
        <p>Loading product...</p>
      </div>
    );
  }

  if (!product) {
    return <Navigate to="/jewellery" replace />;
  }

  const { images, title, description, originalPrice, offerPrice, similarProducts } = product;
  const discount = originalPrice > offerPrice
    ? Math.round(((originalPrice - offerPrice) / originalPrice) * 100)
    : 0;

  const productItem = {
    id: product.id,
    name: title,
    price: offerPrice,
    img: images?.[0],
    link: pathname,
    size: product.size || "Free Size",
    stockQuantity: product.stockQuantity || 0
  };

  const cartQty = cart.find((x) => x.id === product.id)?.qty || 0;
  const wished = isWished(product.id);

  const scroll = (dir) => {
    const scrollAmount = 320;
    if (!sliderRef.current) return;
    sliderRef.current.scrollBy({
      left: dir === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  const openRating = () => {
    // If they already have a rating, it will show up when they click open
    setReviewText("");
    setSubmittedMsg("");
    setShowRatingModal(true);
  };

  const closeRating = () => {
    setShowRatingModal(false);
    setHoverRating(0);
  };

  const handleSelectRating = (val) => {
    setUserRating(val);
  };

  const submitRating = async () => {
    if (!product || !userRating) return;
    setSubmittedMsg("Saving rating...");
    const { data, error } = await submitProductReview(product.id, userRating, reviewText, oldUserRating);
    
    if (data) {
        // Update live details seamlessly
        setProduct(prev => ({ ...prev, rating: data.newRating, review_count: data.newCount }));
        localStorage.setItem(`rated_${productId}`, userRating);
        setOldUserRating(userRating);
        setSubmittedMsg(oldUserRating ? "Your rating was successfully updated!" : "Thanks! Your rating has been saved.");
    } else {
        setSubmittedMsg("Failed to save rating. Try again.");
    }
    
    setTimeout(() => closeRating(), 1500);
  };

  return (
    <div className="product-page">
      <div className="product-container">
        <div className="image-section">
          <div className="main-image">
            <img
              src={selectedImg || images[0]}
              alt={title}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='450' viewBox='0 0 400 450'%3E%3Crect fill='%23f3f3f3' width='400' height='450'/%3E%3Ctext fill='%23999' font-family='sans-serif' font-size='18' x='50%25' y='50%25' text-anchor='middle' dy='.3em'%3ENo Image%3C/text%3E%3C/svg%3E";
              }}
            />
          </div>
          <div className="gallery-row">
            {images.map((img, i) => (
              <img
                key={i}
                src={img}
                onClick={() => setSelectedImg(img)}
                alt=""
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect fill='%23f3f3f3' width='100' height='100'/%3E%3Ctext fill='%23999' font-size='10' x='50%25' y='50%25' text-anchor='middle' dy='.3em'%3E?%3C/text%3E%3C/svg%3E";
                }}
              />
            ))}
          </div>
        </div>

        <div className="details-section">
          <h1 className="product-title">{title ? title.toLowerCase() : ""}</h1>
          <p className="description">{description}</p>

          <div className="price-box">
            <span className="offer-price">₹{offerPrice}.00</span>
            <span className="original-price">₹{originalPrice}</span>
            <span className="discount">{discount}% OFF</span>
          </div>

          <p className="tax-text">Inclusive of all taxes</p>

          <div className="size-section">
            <h4>Size:</h4>
            <button className="size-btn active">Free Size</button>
          </div>

          <div className="button-group">
            {cartQty === 0 ? (
              <button
                type="button"
                className="cart-btn"
                onClick={() => addToCart(productItem)}
                disabled={(product.stockQuantity || 0) <= 0}
              >
                {(product.stockQuantity || 0) <= 0 ? "Out of Stock" : "Add to Cart"}
              </button>
            ) : (
              <div className="product-qty-wrap">
                <button
                  type="button"
                  className="qty-btn"
                  onClick={() => decQty(product.id)}
                  aria-label="Decrease"
                >
                  −
                </button>
                <span className="qty-num">{cartQty}</span>
                <button
                  type="button"
                  className="qty-btn"
                  onClick={() => {
                    if (cartQty < (product.stockQuantity || 0)) {
                      incQty(product.id);
                    } else {
                      alert("Cannot add more than available stock.");
                    }
                  }}
                  disabled={cartQty >= (product.stockQuantity || 0)}
                  aria-label="Increase"
                  style={{ opacity: cartQty >= (product.stockQuantity || 0) ? 0.5 : 1, cursor: cartQty >= (product.stockQuantity || 0) ? 'not-allowed' : 'pointer' }}
                >
                  +
                </button>
              </div>
            )}
            <button
              type="button"
              className="buy-btn"
              onClick={() => {
                if ((product.stockQuantity || 0) <= 0) {
                  alert("Product is out of stock");
                  return;
                }
                if (cartQty === 0) addToCart(productItem);
                navigate("/cart");
              }}
              disabled={(product.stockQuantity || 0) <= 0}
              style={{ opacity: (product.stockQuantity || 0) <= 0 ? 0.5 : 1, cursor: (product.stockQuantity || 0) <= 0 ? 'not-allowed' : 'pointer' }}
            >
              Buy Now
            </button>
            <button
              type="button"
              className={`wishlist-btn ${wished ? "wished" : ""}`}
              onClick={() => toggleWish(productItem)}
              aria-label={wished ? "Remove from wishlist" : "Add to wishlist"}
            >
              {wished ? "♥" : "♡"}
            </button>
          </div>

          <div className="rating-section">
            <div className="stars" aria-label="Product rating">
              {[1, 2, 3, 4, 5].map((s) => (
                <span
                  key={s}
                  className={`star ${Number(product.rating) >= s ? "filled" : Number(product.rating) >= (s - 0.5) ? "half-filled" : ""}`}
                  aria-label={`${s} star`}
                >
                  ★
                </span>
              ))}
            </div>
            <span className="rating-value">
              {(product.rating && isFinite(product.rating)) ? Number(product.rating).toFixed(1) : "0.0"} <span style={{ fontSize: "14px", color: "#666", fontWeight: "normal" }}>({Math.max(0, parseInt(product.review_count) || 0)})</span>
            </span>
            <button type="button" className="rate-btn" onClick={openRating}>
              Rate this product
            </button>
          </div>

          {showRatingModal && (
            <div className="rating-overlay" onClick={closeRating}>
              <div className="rating-modal" onClick={(e) => e.stopPropagation()}>
                <div className="rating-modal-head">
                  <h3>Rate this product</h3>
                  <button className="close-btn" type="button" onClick={closeRating}>
                    ×
                  </button>
                </div>
                <div className="modal-stars">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <button
                      key={s}
                      type="button"
                      className={`star big ${(hoverRating || userRating) >= s ? "filled" : ""}`}
                      onMouseEnter={() => setHoverRating(s)}
                      onMouseLeave={() => setHoverRating(0)}
                      onClick={() => handleSelectRating(s)}
                      aria-label={`${s} star`}
                    >
                      ★
                    </button>
                  ))}
                </div>
                <p className="modal-rating-text">
                  Your rating: <b>{userRating ? userRating.toFixed(1) : "0.0"}</b>
                </p>
                <textarea
                  className="review-box"
                  rows="4"
                  placeholder="Write a short review (optional)..."
                  value={reviewText}
                  onChange={(e) => setReviewText(e.target.value)}
                />
                {submittedMsg && <p className="submit-msg">{submittedMsg}</p>}
                <div className="modal-actions">
                  <button type="button" className="modal-cancel" onClick={closeRating}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="modal-submit"
                    onClick={submitRating}
                    disabled={!userRating}
                  >
                    Submit
                  </button>
                </div>
              </div>
            </div>
          )}

          <Link to="/jewellery" className="back-link">
            ← Back to Jewellery
          </Link>
        </div>
      </div>

      <div className="similar-wrapper">
        <h2 className="similar-heading">You May Also Like</h2>
        <div className="carousel-container">
          <button className="nav-btn left" onClick={() => scroll("left")}>
            ‹
          </button>
          <div className="carousel-track" ref={sliderRef}>
            {(similarProducts || []).map((p, i) => {
              const simItem = { id: p.id || p.link, name: p.name, price: p.price, img: p.img, link: p.link };
              const simWished = isWished(simItem.id);
              return (
                <div key={i} className="similar-card">
                  <Link to={p.link ? (p.link.startsWith('/product') ? p.link : `/product${p.link.startsWith('/') ? '' : '/'}${p.link}`) : "#"} className="similar-card-link">
                    <div className="similar-img">
                      <img
                        src={p.img}
                        alt={p.name}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200' viewBox='0 0 200 200'%3E%3Crect fill='%23f3f3f3' width='200' height='200'/%3E%3Ctext fill='%23999' font-size='14' x='50%25' y='50%25' text-anchor='middle' dy='.3em'%3ENo Image%3C/text%3E%3C/svg%3E";
                        }}
                      />
                    </div>
                    <div className="similar-info">
                      <p className="similar-title">{p.name}</p>
                      <p className="similar-price">₹{p.price}</p>
                    </div>
                  </Link>
                  <div className="similar-actions">
                    <button
                      type="button"
                      className="similar-add-cart"
                      onClick={(e) => { e.preventDefault(); addToCart(simItem); }}
                    >
                      Add to Cart
                    </button>
                    <button
                      type="button"
                      className={`similar-wish ${simWished ? "wished" : ""}`}
                      onClick={(e) => { e.preventDefault(); toggleWish(simItem); }}
                      aria-label={simWished ? "Remove from wishlist" : "Add to wishlist"}
                    >
                      {simWished ? "♥" : "♡"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          <button className="nav-btn right" onClick={() => scroll("right")}>
            ›
          </button>
        </div>
      </div>
    </div>
  );
}
